/**
 * `AW6` · El botón «Descargar CSV» de la pantalla «Conversaciones».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * LAS DECISIONES QUE ESTE ARCHIVO LLEVA CON `guard`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ① **El archivo NO se arma en el navegador.** Este componente no recibe ni
 *   toca las filas de la tabla (`MensajeListado`): recibe un filtro y delega en
 *   `borde.descargar(filtro)`, que es quien arma la ruta y baja el archivo con
 *   la primitiva `descargarArchivo` de `R4-UI0`. El CSV lo produce `admin-web`
 *   (`AP9`), contra `COLUMNAS_DEL_CSV`, y deja su fila de auditoría (`AP10`).
 *   Armarlo acá con las filas en memoria exportaría la página en vez del filtro,
 *   metería los nombres de los documentos por la puerta que `CC3` cerró, y
 *   borraría la fila de auditoría: el archivo se bajaría igual y nadie sabría
 *   quién se lo llevó.
 *
 * ② **El filtro que viaja es el que la tabla tiene APLICADO, no el que el
 *   formulario tiene escrito.** Este componente recibe `filtro` por prop desde
 *   `tabla.tsx`, que es el MISMO objeto `filtroAplicado` que produjo la
 *   respuesta que se está mirando. No lee los campos del formulario: un operador
 *   que cambia el rango y aprieta descargar sin apretar «Aplicar filtro» se
 *   lleva el archivo de la tabla que está viendo, no el de un filtro que todavía
 *   no se aplicó.
 *
 * ③ ⭐ `R4-UIC1` · **el botón con estado es la PRIMITIVA `BotonDeDescarga`**, no
 *   un `<button>` propio (ADR-0386). La primitiva deshabilita y dice
 *   «Descargando…» mientras la `Promise` de `descargar` está en vuelo (CA-13) y
 *   entrega el rechazo por `onError` (CA-14) para que la pantalla pinte su aviso
 *   debajo del encabezado. Por eso este componente NO tiene clases propias: el
 *   `className="boton conversaciones__descargar"` se fue con el botón propio; su
 *   regla de `estilos.css` ahora nombra al botón por su lugar en el encabezado.
 *
 * El botón no ofrece «exportar la página actual», ni selector de columnas, ni
 * casilla de anonimización. Las columnas son las diez de `COLUMNAS_DEL_CSV` y la
 * anonimización es `[CONFIG]` con `❓Q23` abierta (dueño Elige / BID).
 */

import type { JSX } from "react"
import type { FiltroDeLaPantalla } from "./borde-conversaciones.ts"
import { useBordeConversaciones } from "../contexto-borde-conversaciones.tsx"
import { BotonDeDescarga } from "../boton-de-descarga.tsx"

/**
 * ⭐ `R3-UI1` · el filtro es el de la PANTALLA (`FiltroDeLaPantalla`) y no el del
 * módulo (`FiltroDeMensajes`): los días viajan como días y el que los convierte a
 * instantes de Chile es el servidor. Ver ⟦los-dias-viajan-como-dias⟧ en
 * `borde-conversaciones.ts`. Este componente igual no lo mira: lo pasa entero.
 *
 * ⭐ `R4-UIC1` · `onIntento` se llama ANTES de disparar la descarga y `onFallo`
 * cuando el borde rechaza: la pantalla (`tabla.tsx`) los usa para borrar y
 * mostrar su aviso propio (D5/CA-14). Se nombran así y no `onFalloDescarga`
 * booleano para que el estado que condiciona el aviso viva en `tabla.tsx` con
 * su setter, que es quien lo limpia al reintentar.
 */
export function BotonDescargarCsv({
  filtro,
  onIntento,
  onFallo,
}: {
  readonly filtro: FiltroDeLaPantalla
  readonly onIntento: () => void
  readonly onFallo: () => void
}): JSX.Element {
  const borde = useBordeConversaciones()

  return (
    <BotonDeDescarga
      rotulo="Descargar CSV"
      onError={onFallo}
      accion={() => {
        onIntento()
        return borde.descargar(filtro)
      }}
    />
  )
}
